<template>
  <div class="feedback-form">
    <!-- Dev controls -->
<!--
    <div class="dev-panel">
      <label class="dev-label">Test Scenario:</label>
      <select v-model="testScenario" class="dev-select">
        <option value="success">Success - Feedback Submitted</option>
        <option value="invalid-otp">Invalid OTP</option>
        <option value="expired-otp">Expired OTP</option>
        <option value="too-many-attempts">Too Many OTP Attempts</option>
        <option value="rate-limit">Rate Limit Exceeded</option>
        <option value="server-error">Server Error</option>
      </select>
    </div>
-->

    <div class="form-container">
      <h2 class="title">Verify & Submit Your Message</h2>
      <p>Check your email for the OTP. Enter it below to proceed.</p>

      <div class="input-group">
        <div class="otp-section">
          <input
              v-model="otp"
              type="text"
              placeholder="Enter OTP"
              maxlength="6"
              :disabled="isLoading"
              required
          />
          <button
              @click="resendOTP"
              class="resend-button"
              :disabled="isResending || resendCooldown > 0"
          >
            <span v-if="isResending">Sending...</span>
            <span v-else-if="resendCooldown > 0">Resend in {{ resendCooldown }}s</span>
            <span v-else>Resend OTP</span>
          </button>
        </div>

        <div class="message-section">
          <textarea
              v-model="message"
              placeholder="Describe your issue... (minimum 10 characters)"
              :disabled="isLoading"
              required
          ></textarea>
          <div class="character-counter" :class="{ 'error': message.length < 10 }">
            {{ message.length }}/10 characters minimum
          </div>
        </div>
      </div>

      <div class="button-group">
        <button
            @click="submitFeedback"
            :disabled="!isValid || isLoading"
            class="primary-button"
        >
          <span v-if="isLoading" class="loading-spinner"></span>
          <span>{{ isLoading ? 'Submitting...' : 'Submit' }}</span>
        </button>
      </div>

      <p v-if="errorMessage" class="error-message">
        <span class="error-icon">⚠️</span>
        {{ errorMessage }}
      </p>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  props: ["email"],
  data() {
    return {
      otp: "",
      message: "",
      errorMessage: "",
      testScenario: "success",
      isLoading: false,
      isResending: false,
      resendCooldown: 0,
      resendTimer: null
    };
  },
  computed: {
    isValid() {
      return /^\d{6}$/.test(this.otp) && this.message.length >= 10;
    }
  },
  methods: {
    ///
    async submitFeedback() {

      if (!this.isValid) return;

      this.isLoading = true;
      this.errorMessage = "";

      try {
        // const endpoint = `http://localhost:8088/submit-feedback/${this.testScenario}`;
        const endpoint = `https://659xtx8fv1.execute-api.us-east-1.amazonaws.com/Beta/submit-support`;

        const response = await axios.post(endpoint, {
          email: this.email,
          otp: this.otp,
          message: this.message,
        });

        // Our Lambda returns a message property on success
        // if (response.data.message === "Support request submitted successfully") {
        if (response.data.success) {

          // this.$emit("submitted");
          this.$emit("submitted", this.message);  // Pass the message here

        } else {
          this.errorMessage = response.data.message;
        }

      } catch (error) {
        if (error.response?.data?.error) {
          this.errorMessage = error.response.data.message;
        } else {
          this.errorMessage = "Network error. Please check your connection.";
        }
      } finally {
        this.isLoading = false;
      }
    },

    ///
    async resendOTP() {

      if (this.isResending || this.resendCooldown > 0) return;

      this.isResending = true;
      this.errorMessage = "";

      try {

        /// https://659xtx8fv1.execute-api.us-east-1.amazonaws.com/Beta/submit-support
        // const endpoint = `http://localhost:8088/requestOTP/success`;

        const response = await axios.post("https://659xtx8fv1.execute-api.us-east-1.amazonaws.com/Beta/submit-support",
            {
              email: this.email,
              otp: this.otp,
              message: this.message,
            }
        );

        if (response.data.status === "success") {
          this.$emit("submitted");
        } else {
          this.errorMessage = response.data.message;
        }

        // Start cooldown timer
        this.resendCooldown = 30; // 30 seconds cooldown
        this.startCooldownTimer();

      } catch (error) {

        ///
        if (error.response?.data?.error) {
          this.errorMessage = error.response.data.error;
        } else {
          this.errorMessage = "Failed to resend OTP. Please try again.";
        }

        ///
      } finally {
        this.isResending = false;
      }

    },

    startCooldownTimer() {
      this.resendTimer = setInterval(() => {
        if (this.resendCooldown > 0) {
          this.resendCooldown--;
        } else {
          clearInterval(this.resendTimer);
        }
      }, 1000);
    }
  },
  beforeUnmount() {
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
    }
  }

  ///
};
</script>


<style scoped>
@import '@/assets/styles/supportForm.css';

/* Base Form Container Styles */
.feedback-form {
  margin: 0 auto;
  padding: 20px;
}

.form-container {
  background: white;
  border-radius: 12px;
  padding: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.title {
  color: #2c3e50;
  font-size: 1.5rem;
  margin-bottom: 24px;
  font-weight: 600;
}

/* Input Groups */
.input-group {
  margin-bottom: 20px;
}

/* Basic Input Styles */
input, textarea {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
}

input:focus, textarea:focus {
  border-color: #4299e1;
  outline: none;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.2);
}

/* Textarea Specific */
textarea {
  min-height: 120px;
  resize: vertical;
}

/* Button Styles */
.button-group {
  margin-top: 24px;
}

.primary-button {
  width: 100%;
  padding: 12px 24px;
  background: #4299e1;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.primary-button:hover:not(:disabled) {
  background: #3182ce;
}

.primary-button:disabled {
  background: #a0aec0;
  cursor: not-allowed;
}

/* Error Messages */
.error-message {
  margin-top: 16px;
  color: #e53e3e;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: #fff5f5;
  border-radius: 6px;
}

/* Loading Spinner */
.loading-spinner {
  width: 20px;
  height: 20px;
  border: 3px solid #ffffff;
  border-top: 3px solid transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* Dev Panel Styles */
.dev-panel {
  background: #1a202c;
  color: white;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 20px;
}

.dev-label {
  display: block;
  margin-bottom: 8px;
  font-size: 0.875rem;
  color: #a0aec0;
}

.dev-select {
  width: 100%;
  padding: 8px;
  background: #2d3748;
  color: white;
  border: 1px solid #4a5568;
  border-radius: 4px;
}

/* FeedbackForm Specific Styles */
.otp-section {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
}

.resend-button {
  white-space: nowrap;
  padding: 8px 16px;
  background: #718096;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.resend-button:disabled {
  background: #CBD5E0;
  cursor: not-allowed;
}

.message-section {
  position: relative;
}

.character-counter {
  position: absolute;
  bottom: -20px;
  right: 0;
  font-size: 0.75rem;
  color: #718096;
}

.character-counter.error {
  color: #e53e3e;
}

/* Animations */
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>